import {
    Alignment, LocalUserSettings, LogManager, ModelRow, Navigation, NavOptions, ObjectUtil, StringUtil, UrlUtil
} from "@mcleod/core";
import { ClickEvent } from "../../events/ClickEvent";
import { ContextMenuLabelProps } from "../contextmenu/ContextMenuLabelProps";
import { CrudDecorator } from "../../page/decorators/CrudDecorator";
import { CrudDecoratorCloseEvent } from "../../events/CrudDecoratorCloseEvent";
import { CrudDecoratorProps } from "../../page/decorators/CrudDecoratorProps";
import { DataHeaderProps } from "../dataheader/DataHeaderProps";
import { DataSourceMode } from "../../databinding/DataSource";
import { Layout } from "../layout/Layout";
import { LayoutProps } from "../layout/LayoutProps";
import { OpenType } from "../../page/openButtons/OpenType";
import { Snackbar } from "../../page/Snackbar";
import { Table } from "./Table";
import { TableRow } from "./TableRow";

const log = LogManager.getLogger("components.table.TableCrudManager");

export class TableCrudManager {
    private crudDecorator: CrudDecorator;
    private _defaultOpenType: OpenType;
    protected readonly table: Table;
    private openContextMenuItemCreators: Map<OpenType, (tableRow: TableRow) => Partial<ContextMenuLabelProps>>;
    overrideAddUrlParams: () => any;
    overrideUrlParams: (tableRow?: TableRow) => any;
    determineDetailKeyValue: (tableRow: TableRow) => string;

    constructor(table: Table) {
        this.table = table;
        this.openContextMenuItemCreators = this.createOpenContextMenuItemCreatorMap();
    }

    async showAdd() {
        const urlParams = this.overrideAddUrlParams ? this.overrideAddUrlParams() : {};
        const url = "/" + this.table.addLayout + UrlUtil.buildQueryString({ mode: "add", ...urlParams });
        const dataHeaderProps: Partial<DataHeaderProps> = {
            onExecute: (addedRow: ModelRow) => {
                if (!this.table.dataSource.subscribeToChanges) {
                    this.table.dataSource.addRow(addedRow);
                }
            },
            showDelete: this.table.allowDelete
        };
        this.showCrud(DataSourceMode.ADD, {pseudoNavigateUrl: url}, dataHeaderProps);
    }

    get allowAdvancedSearch(): boolean {
        return this.table.allowAdvancedSearch && this.getLayoutNameForMode(DataSourceMode.SEARCH) != null;
    }

    showSearch() {
        const dataHeaderProps: Partial<DataHeaderProps> = {
            onExecute: (filterValues: ModelRow) => {
                this.table.textboxSearch.clear();
                this.table.textboxFilter.clear();
                this.table.dataSource.search(filterValues);
            }
        };
        this.showCrud(DataSourceMode.SEARCH, null, dataHeaderProps);
    }

    get defaultOpenType(): OpenType {
        if (this._defaultOpenType == null) {
            this._defaultOpenType = this.getDefaultOpenTypeFromLocalStorage() ?? OpenType.SAME_TAB;
        }
        return this._defaultOpenType;
    }

    set defaultOpenType(value: OpenType) {
        if (this._defaultOpenType === value) {
            return;
        }
        this._defaultOpenType = value;
        this.syncDefaultOpenType();
    }

    private syncDefaultOpenType() {
        // Avoid saving defaultOpenType to local storage if it's the same as the implicit default value of
        // OpenType.SAME_TAB
        const valueToStore = OpenType.SAME_TAB === this.defaultOpenType ? null : this._defaultOpenType;
        LocalUserSettings.set(this.getOpenTypeLocalStorageKey(), valueToStore);
        this.table.headingPanel.syncDefaultDetailButton();
    }

    private getDefaultOpenTypeFromLocalStorage(): OpenType | null {
        const storedValue = LocalUserSettings.getString(this.getOpenTypeLocalStorageKey());
        if (StringUtil.isEmptyString(storedValue))
            return null;

        if (!ObjectUtil.isEnumValue(OpenType, storedValue)) {
            LocalUserSettings.set(this.getOpenTypeLocalStorageKey(), null);
            return null;
        }

        return storedValue;
    }

    private getOpenTypeLocalStorageKey(): string {
        const keyPrefix = this.table.getUniqueIdentifier();
        return keyPrefix == null ? null : keyPrefix + "_defaultOpenType";
    }

    get allowDetail(): boolean {
        return this.table.allowDetail && this.getLayoutNameForMode(DataSourceMode.UPDATE) != null;
    }

    getOpenContextMenuItems(row: TableRow, excludedOpenTypes: OpenType[] = []): Partial<ContextMenuLabelProps>[] {
        if (row == null) {
            return [];
        }
        const rowIsSelected = this.table.selectedRows?.includes(row) ?? false;
        const selectedRowCount = this.table.selectedRowCount;
        return [OpenType.SAME_TAB, OpenType.NEW_TAB_WITH_SWITCH, OpenType.NEW_WINDOW]
            .filter(openType => !excludedOpenTypes.includes(openType))
            .map(openType => this.createContextMenuItem(row, openType, rowIsSelected, selectedRowCount))
            .filter(item => item != null);
    }

    private createContextMenuItem(row: TableRow, openType: OpenType, rowIsSelected: boolean, selectedRowCount: number):
        Partial<ContextMenuLabelProps> | null {
        const item = this.openContextMenuItemCreators.get(openType)?.(row);
        if (item == null) {
            return null;
        }
        if (openType === OpenType.SAME_TAB) {
            item.enabled = item.enabled !== false && rowIsSelected && selectedRowCount === 1;
        } else {
            item.enabled = item.enabled !== false && (!rowIsSelected || selectedRowCount === 1);
        }
        return item;
    }

    private createOpenContextMenuItemCreatorMap(): Map<OpenType, (row: TableRow) => Partial<ContextMenuLabelProps>> {
        const result = new Map();
        result.set(OpenType.SAME_TAB, this.createDefaultOpenInSameTabContextMenuItem);
        result.set(OpenType.NEW_TAB_WITH_SWITCH, this.createDefaultOpenInNewTabContextMenuItem);
        result.set(OpenType.NEW_WINDOW, this.createDefaultOpenInNewWindowContextMenuItem);
        return result;
    }

    setOpenContextMenuItemOverride(openType: OpenType, method: (row: TableRow) => Partial<ContextMenuLabelProps>) {
        this.openContextMenuItemCreators.set(openType, method);
    }

    private createDefaultOpenInSameTabContextMenuItem = (row: TableRow): Partial<ContextMenuLabelProps> => {
        return this.createDefaultOpenContextMenuItem(row, OpenType.SAME_TAB, "Open");
    }

    private createDefaultOpenInNewTabContextMenuItem = (row: TableRow): Partial<ContextMenuLabelProps> => {
        return this.createDefaultOpenContextMenuItem(row, OpenType.NEW_TAB_WITH_SWITCH, "Open in New Tab");
    }

    private createDefaultOpenInNewWindowContextMenuItem = (row: TableRow): Partial<ContextMenuLabelProps> => {
        return this.createDefaultOpenContextMenuItem(row, OpenType.NEW_WINDOW, "Open in New Window");
    }

    private createDefaultOpenContextMenuItem(row: TableRow, openType: OpenType, text: string):
        Partial<ContextMenuLabelProps> {
        if (this.table.allowDetail) {
            return {
                onClick: () => {
                    this.defaultOpenType = openType;
                    this.showDetail(openType, row);
                },
                text
            };
        }
        return null;
    }

    private getPopoutNavOptions(): Partial<NavOptions> {
        const windowSize = {
            width: 1400,
            height: 700,
        };
        const loc = {
            left: ((window.screen as any).availLeft + (window.screen.availWidth / 2)) - (windowSize.width / 2),
            top: ((window.screen as any).availTop + (window.screen.availHeight / 2)) - (windowSize.height / 2)
        };
        return { left: loc.left, top: loc.top, height: 700, width: 1400, newTab: true, windowDecorators: false };
    }

    showDetailFromClick(event: ClickEvent) {
        if (event.hasModifiers({ ctrlKey: true, shiftKey: true }))
            this.showDetail(OpenType.NEW_TAB_WITH_SWITCH);
        else if (event.hasModifiers({ shiftKey: true }))
            this.showDetail(OpenType.NEW_WINDOW);
        else if (event.hasModifiers({ ctrlKey: true }))
            this.showDetail(OpenType.NEW_TAB_NO_SWITCH);
        else
            this.showDetail(this.defaultOpenType);
    }

    async showDetail(openType: OpenType, tableRow: TableRow = this.table?.selectedRow) {
        if (this.allowDetail === false) {
            log.debug(this.table, `Unable to show detail: allowDetail: ${this.allowDetail}, detailLayout: ${this.table.detailLayout}`);
            return;
        }
        const selectedRowData = tableRow?.data as ModelRow;
        if (selectedRowData == null) {
            return;
        }
        const validateRowDetailResult = this.table.validateRowDetail?.(tableRow);
        if (!StringUtil.isEmptyString(validateRowDetailResult)) {
            log.debug(this.table, "Selected row not allowed to show detail: %s", validateRowDetailResult);
            Snackbar.showWarningSnackbar(validateRowDetailResult);
            return;
        }

        let key: string;
        if (this.determineDetailKeyValue != null) {
            key = this.determineDetailKeyValue(tableRow);
        } else {
            const keyData = selectedRowData.getKeyData();
            const keys = Object.keys(keyData);
            key = keyData[keys[0]] as string;
        }
        if (key == null) {
            log.debug(this.table, "Unable to determine key for selected row");
            return;
        }

        this.table.detailLayout = this.table.detailLayout;
        const urlParam = { mode: "update", ...(this.overrideUrlParams != null ? this.overrideUrlParams(this.table.selectedRow) : { key }) }
        const url = "/" + this.table.detailLayout + UrlUtil.buildQueryString(urlParam);
        if (openType === OpenType.SAME_TAB) {
            const dataHeaderProps: Partial<DataHeaderProps> = {
                showDelete: tableRow.allowDelete === true,
                onExecute: async (updatedRow: ModelRow) => {
                    this.table.resolveRowEdit(this.table?.selectedRow?.data, updatedRow);
                }
            };
            this.showCrud(DataSourceMode.UPDATE, { key: key, pseudoNavigateUrl: url }, dataHeaderProps);
        }
        else if (openType === OpenType.NEW_WINDOW)
            Navigation.navigateTo(url, this.getPopoutNavOptions());
        else if (openType === OpenType.NEW_TAB_NO_SWITCH) {
            Navigation.navigateTo(url, { newTab: true });
            window.focus();
        }
        else
            Navigation.navigateTo(url, { newTab: true });
    }

    public showCrud(mode: DataSourceMode, crudDecoratorProps: Partial<CrudDecoratorProps>,
        dataHeaderProps: Partial<DataHeaderProps>) {
        const layoutName = this.getLayoutNameForMode(mode);
        if (layoutName == null) {
            log.debug(this.table, "No layout found for mode " + mode);
            return;
        }
        const layoutProps: Partial<LayoutProps> = {};
        let slideDirection = Alignment.BOTTOM;
        if (this.table.crudDecoratorWidth) {
            layoutProps.width = this.table.crudDecoratorWidth;
            slideDirection = Alignment.RIGHT;
        }
        const layout = Layout.getLayout(layoutName, layoutProps);
        this.crudDecorator = new CrudDecorator({
            layout: layout,
            mode: mode,
            headerProps: {
                showClose: true,
                showSaveAndClose: true,
                doAfterClose: (event: CrudDecoratorCloseEvent) => this.doAfterCrudClose(event),
                ...dataHeaderProps,
            },
            ...crudDecoratorProps
        });
        this.crudDecorator.slideIn({ speed: 200, direction: slideDirection });
    }

    private doAfterCrudClose(event: CrudDecoratorCloseEvent) {
        if (event.userDeleted() === true) {
            const deletedRow = event.dataHeader.dataSource.activeRow;
            log.debug("Attempting to remove table row with data: %o", deletedRow);
            const rowIndexInDataSource = this.table.dataSource.findRowInData(deletedRow);
            if (rowIndexInDataSource >= 0) {
                const rowInDataSource = this.table.dataSource.data[rowIndexInDataSource];
                log.debug("Matching row found in DataSource: %o", rowInDataSource);
                this.table.dataSource._deleteTableRow(rowInDataSource, !this.table.persistChangesImmediately);
            }
            else
                log.debug("Could not remove row from table; matching row was not found in the DataSource");
        }
        this.table.fireAfterCrudCloseListener(event);
    }

    private getLayoutNameForMode(mode: DataSourceMode): string {
        switch (mode) {
            case DataSourceMode.ADD: return this.table.addLayout;
            case DataSourceMode.UPDATE: return this.table.detailLayout;
            case DataSourceMode.SEARCH: return this.table.searchLayout;
            default: return this.table.generalLayout;
        }
    }
}
